/**
 * English locale data for ions — name and uses only.
 * Key = atomic number. Arrays are in the same order as ions-data.ts.
 */

export interface IonLocaleData {
  name: string;
  uses: string;
}

export const elementIonsEn: Record<number, IonLocaleData[]> = {
  1: [
    { name: 'Hydron (Proton)', uses: 'Aqueous acids, acid catalysis, membrane transport, buffer solution chemistry, hydrolysis reactions.' },
    { name: 'Hydride', uses: 'Chemical reduction, organic synthesis, hydrogen storage (metal hydrides), fuel cells.' },
  ],
  2: [],

  3: [
    { name: 'Lithium', uses: 'Lithium-ion batteries (smartphones, electric vehicles), bipolar disorder treatment, ceramics, lubricants.' },
  ],
  4: [
    { name: 'Beryllium', uses: 'High-performance lightweight alloys (aerospace), X-ray windows, nuclear (neutron moderator).' },
  ],
  5: [
    { name: 'Boron', uses: 'Ceramic borides, catalysis (Lewis acids), doped semiconductors, anticancer drugs (BNCT).' },
  ],
  6: [
    { name: 'Carbide', uses: 'Metal carbides (steel, TiC, SiC), refractories, industrial abrasives.' },
  ],
  7: [
    { name: 'Nitride', uses: 'Silicon nitride (semiconductors), aluminium nitride (power electronics), hard coatings.' },
  ],
  8: [
    { name: 'Oxide', uses: 'Metal oxides (corrosion, ceramics, catalysis), SOFC fuel cell electrolytes, pigments.' },
    { name: 'Oxide radical', uses: 'Gas-phase oxidation, atmospheric chemistry, ozonolysis, industrial plasmas.' },
  ],
  9: [
    { name: 'Fluoride', uses: 'Cavity prevention (fluoridated water, toothpaste), industrial fluorination, battery electrolytes, refrigerants (HFCs).' },
  ],
  10: [],

  11: [
    { name: 'Sodium', uses: 'Electrolyte balance (blood, sweat), nerve transmission, table salt (NaCl), caustic soda (NaOH), aluminium production.' },
  ],
  12: [
    { name: 'Magnesium', uses: 'Enzyme cofactor (>300 enzymes), chlorophyll, muscle contraction, lightweight alloys for automotive and aerospace.' },
  ],
  13: [
    { name: 'Aluminium', uses: 'Water coagulation (alum), antacids, catalysts (alumina), antiperspirants, pigments (aluminate).' },
  ],
  14: [
    { name: 'Silicon(IV)', uses: 'Silicates (glass, ceramics, cement), zeolites (catalysis), SiO₂ (microelectronics).' },
    { name: 'Silicide', uses: 'Metal silicides (alloys, semiconductors), MoSi₂ (high-temperature resistors).' },
  ],
  15: [
    { name: 'Phosphide', uses: 'III-V semiconductors (GaP, InP, LEDs), pesticides (phosphine PH₃), phosphorus alloys.' },
    { name: 'Phosphorus(III)', uses: 'Phosphorous acid, phosphite ligands in organometallic catalysis, organic synthesis.' },
    { name: 'Phosphorus(V)', uses: 'Phosphoric acid (fertilisers, beverages, metal treatment), biological phosphates (DNA, ATP, bone).' },
  ],
  16: [
    { name: 'Sulfide', uses: 'Sulfide minerals (galena, pyrite), Na-S batteries, solid lubricant (MoS₂), semiconductors (CdS).' },
    { name: 'Sulfur(IV)', uses: 'Sulfur dioxide SO₂ (food preservative E220, bleaching), synthesis of sulfurous acid.' },
    { name: 'Sulfur(VI)', uses: 'Sulfuric acid H₂SO₄ (major chemical industry, fertilisers, lead-acid batteries), sulfates (plaster, alum).' },
  ],
  17: [
    { name: 'Chloride', uses: 'Table salt (NaCl), osmotic regulation, Cl₂ production (disinfection, PVC), hydrochloric acid HCl.' },
    { name: 'Chlorine(V)', uses: 'Chloric acid, perchlorate (oxidiser in fireworks and rocket propellants).' },
    { name: 'Chlorine(VII)', uses: 'Perchloric acid (chemical analysis, catalysis), potassium perchlorate (hyperthyroidism treatment).' },
  ],
  18: [],

  19: [
    { name: 'Potassium', uses: 'Heart rhythm, nerve transmission, fertilisers (potash), potassium salts (KCl, KNO₃), cellular osmosis.' },
  ],
  20: [
    { name: 'Calcium', uses: 'Bone and teeth formation (hydroxyapatite), muscle contraction, cell signalling, cement (CaO), cheese.' },
  ],
  21: [
    { name: 'Scandium', uses: 'High-performance Al-Sc alloys (aerospace, sport), metal halide lamps (HID lighting), yttrium scandate (laser).' },
  ],
  22: [
    { name: 'Titanium(II)', uses: 'Reaction intermediate, Ziegler-Natta catalysis (polyethylene, polypropylene).' },
    { name: 'Titanium(III)', uses: 'Reducing agent in organic chemistry, textile treatment, catalysis.' },
    { name: 'Titanium(IV)', uses: 'TiO₂ (white pigment, solar photocatalysis, sunscreen), medical implants, anti-UV coatings, capacitors.' },
  ],
  23: [
    { name: 'Vanadium(II)', uses: 'Vanadium redox batteries (large-scale energy storage), pigments.' },
    { name: 'Vanadium(III)', uses: 'Special steels, catalysis, green and blue pigments for ceramics.' },
    { name: 'Vanadium(IV)', uses: 'Industrial catalysts (SO₂ oxidation), semiconductors (VO₂), superconductors.' },
    { name: 'Vanadium(V)', uses: 'Contact process catalyst (sulfuric acid), corrosion inhibitor, vanadium pentoxide (V₂O₅).' },
  ],
  24: [
    { name: 'Chromium(II)', uses: 'Reducing agent, catalysis, synthesis intermediate (Jones reagent).' },
    { name: 'Chromium(III)', uses: 'Leather tanning, green colorants (chromium oxide), nutritional trivalent chromium, stainless alloys.' },
    { name: 'Chromium(VI)', uses: 'Industrial chrome plating (corrosion protection), yellow/orange pigments, oxidising agent (dichromate) — note: carcinogenic.' },
  ],
  25: [
    { name: 'Manganese(II)', uses: 'Enzyme cofactor, alkaline batteries (MnO₂/Zn), pink pigments, glass decolouring.' },
    { name: 'Manganese(III)', uses: 'Enzymatic catalysis (superoxide dismutase), pigments, mild oxidant in synthesis.' },
    { name: 'Manganese(IV)', uses: 'MnO₂ (positive electrode in Leclanché/alkaline batteries), manganese dioxide (catalyst).' },
    { name: 'Manganese(VII)', uses: 'Permanganate (KMnO₄): disinfectant, powerful oxidant, water treatment, analytical reagent.' },
  ],
  26: [
    { name: 'Iron(II) — Ferrous', uses: 'Haemoglobin and oxygen transport, muscle myoglobin, ferritin (iron storage), iron supplements, green pigments.' },
    { name: 'Iron(III) — Ferric', uses: 'Water coagulation (ferric sulfate), rust (iron oxide), red/yellow pigments (ochre), catalyst (FeCl₃).' },
  ],
  27: [
    { name: 'Cobalt(II)', uses: 'Vitamin B12 (cobalamin), Li-ion batteries (LiCoO₂), cobalt blue (ceramics, glass), Fischer-Tropsch catalyst.' },
    { name: 'Cobalt(III)', uses: 'Therapeutic complexes, pigments, oxidation catalysis, wear-resistant surface coatings.' },
  ],
  28: [
    { name: 'Nickel(II)', uses: 'NiMH and Ni-Cd batteries, electroplating (corrosion resistance), Raney catalysis (hydrogenation), coins.' },
    { name: 'Nickel(III)', uses: 'NiMH battery electrodes, mixed nickel oxides, oxidation catalysts.' },
  ],
  29: [
    { name: 'Copper(I) — Cuprous', uses: 'Fungicide (cuprous oxide), blue/green colorants (ancient Egypt), click catalysis (organic chemistry), ceramics.' },
    { name: 'Copper(II) — Cupric', uses: 'Electrolyte (copper electrodeposition), copper sulfate (Bordeaux mixture, algicide), catalysis, blue-green pigment.' },
  ],
  30: [
    { name: 'Zinc', uses: 'Enzyme cofactor (200+ enzymes), immunity, wound healing, steel galvanisation, zinc-air batteries, sunscreen (ZnO).' },
  ],
  31: [
    { name: 'Gallium(III)', uses: 'III-V semiconductors (GaAs, GaN for blue LEDs and lasers), solar panels, nuclear medical imaging (⁶⁷Ga).' },
  ],
  32: [
    { name: 'Germanium(II)', uses: 'Catalyst, reaction intermediate, infrared optics (GeS).' },
    { name: 'Germanium(IV)', uses: 'Semiconductors (historical transistors), optical fibres (GeO₂), PET catalyst, gamma detectors.' },
  ],
  33: [
    { name: 'Arsenide', uses: 'GaAs semiconductors (telephony, radar), InAs (infrared detectors), AlAs (optoelectronics).' },
    { name: 'Arsenic(III)', uses: 'Arsenic trioxide As₂O₃: treatment of acute promyelocytic leukaemia, historical wood preservative.' },
    { name: 'Arsenic(V)', uses: 'Lead arsenate (historical insecticide), veterinary antiparasitic drugs.' },
  ],
  34: [
    { name: 'Selenide', uses: 'Solar cells (CIGS), semiconductors (CdSe for QDs), rectifiers, photocopiers (amorphous Se).' },
    { name: 'Selenium(IV)', uses: 'Selenious acid (metal brightening, stainless steel etching), red/orange glass.' },
    { name: 'Selenium(VI)', uses: 'Selenates (oxidising agent, analytical), nutritional supplement.' },
  ],
  35: [
    { name: 'Bromide', uses: 'Silver bromide (AgBr, silver photography), potassium bromide (anticonvulsant), flame retardants, drilling fluids.' },
    { name: 'Bromine(III)', uses: 'Brominating agents in organic synthesis, pool water disinfectants (bromochloro).' },
    { name: 'Bromine(V)', uses: 'Potassium bromate (KBrO₃, flour improver — banned in some countries), analytical oxidant.' },
  ],
  36: [
    { name: 'Krypton(II)', uses: 'KrF₂ compounds (super-oxidising fluorinating agent in high-fluorophilicity synthesis), fundamental research.' },
  ],
  37: [
    { name: 'Rubidium', uses: 'Ultra-precise atomic clocks (GPS, telecommunications), photocells (photoelectric effect), BEC research.' },
  ],
  38: [
    { name: 'Strontium', uses: 'Bright-red pyrotechnics (strontianite, Bengal fire), strontium ranelate (osteoporosis treatment), scintillators.' },
  ],
  39: [
    { name: 'Yttrium(III)', uses: 'Yttrium aluminium garnet YAG (surgical laser), red TV phosphors, YBCO superconductors, stabilised zirconia (dental ceramics).' },
  ],
  40: [
    { name: 'Zirconium(IV)', uses: 'Nuclear reactors (fuel cladding, neutron-resistant), cubic zirconia (jewellery), refractories, biomedical implants.' },
  ],
  41: [
    { name: 'Niobium(III)', uses: 'Superconductors (Nb₃Sn, NbTi for MRI and accelerators), catalysis.' },
    { name: 'Niobium(V)', uses: 'High-strength steels (pipelines, construction), niobate capacitors, nonlinear optics (LiNbO₃).' },
  ],
  42: [
    { name: 'Molybdenum(II)', uses: 'Organometallic complexes, catalysis.' },
    { name: 'Molybdenum(III)', uses: 'Catalytic intermediates, reduction of atmospheric nitrogen (nitrogenase).' },
    { name: 'Molybdenum(IV)', uses: 'Solid lubricants (MoS₂), semiconductors (MoSe₂), hydrodesulfurisation catalysts (petroleum refining).' },
    { name: 'Molybdenum(VI)', uses: 'Anti-corrosion and high-strength special steels, refining catalysts, molybdate orange pigment, enzyme cofactor.' },
  ],
  43: [
    { name: 'Technetium(IV)', uses: 'Nuclear medical imaging (⁹⁹ᵐTc, bone, cardiac, renal scintigraphy — the most-used tracer in nuclear medicine).' },
    { name: 'Technetium(VII)', uses: 'Pertechnetate (⁹⁹ᵐTcO₄⁻) as tracer for SPECT medical imaging, corrosion inhibitor.' },
  ],
  44: [
    { name: 'Ruthenium(II)', uses: 'Photosensitive complexes (Grätzel dye-sensitised solar cells), metathesis catalysis (Grubbs), anticancer agents.' },
    { name: 'Ruthenium(III)', uses: 'Catalyst (hydrogenation, oxidation), textile dyes, phase-change memories.' },
    { name: 'Ruthenium(IV)', uses: 'RuO₂ (electrode for chlor-alkali, supercapacitors), selective oxidation catalysis.' },
  ],
  45: [
    { name: 'Rhodium(III)', uses: 'Catalytic converter (NOx, CO, HC conversion), hydroformylation catalysis, electroplating for jewellery.' },
  ],
  46: [
    { name: 'Palladium(II)', uses: 'Cross-coupling catalyst (Suzuki, Heck — Nobel 2010), hydrogenation, catalytic converter, multilayer capacitors (MLCC).' },
    { name: 'Palladium(IV)', uses: 'Wacker oxidation, palladium fluorides, intermediates in advanced catalysis.' },
  ],
  47: [
    { name: 'Silver', uses: 'AgBr/AgCl (silver photography), silver nitrate (antiseptic, ink stains), antimicrobial nanoparticles, coinage.' },
  ],
  48: [
    { name: 'Cadmium(II)', uses: 'Ni-Cd batteries, yellow/red pigments (CdS, CdSe), PVC stabilisers, thin-film photovoltaics (CdTe).' },
  ],
  49: [
    { name: 'Indium(III)', uses: 'ITO (indium tin oxide, touchscreens and LCDs), III-V semiconductors (InP, InAs), low-melting-point solders.' },
  ],
  50: [
    { name: 'Tin(II) — Stannous', uses: 'Stannous chloride (SnCl₂: reducing agent, tin-plating bath), radiographic developing, textile mordant.' },
    { name: 'Tin(IV) — Stannic', uses: 'SnO₂ (transparent conductor for solar panels, gas sensors), PVC stabiliser (organotin compounds), catalysis.' },
  ],
  51: [
    { name: 'Antimonide', uses: 'III-V semiconductors (InSb for thermographic IR detectors), experimental solar cells.' },
    { name: 'Antimony(III)', uses: 'Antimony trioxide Sb₂O₃ (synergistic flame retardant with halogens), white pigment, glass, PET catalysis.' },
    { name: 'Antimony(V)', uses: 'Antimonate (ion exchangers, leishmaniasis treatment), hexafluoroantimonic acid (superacid).' },
  ],
  52: [
    { name: 'Telluride', uses: 'CdTe (thin-film solar panels), PbTe (thermoelectric), phase-change materials (Ge₂Sb₂Te₅, memories).' },
    { name: 'Tellurium(IV)', uses: 'Synthesis of tellurides, tellurium-steel alloys (free-machining), rubber (vulcanisation accelerator).' },
    { name: 'Tellurium(VI)', uses: 'Telluric acid (chemical analysis, organic synthesis), veterinary antiparasitic agents.' },
  ],
  53: [
    { name: 'Iodide', uses: 'Potassium iodide (thyroid protection against radiation, iodised salt), AgI (photography, cloud seeding), antiseptics.' },
    { name: 'Iodine(V)', uses: 'Periodate (selective biochemical oxidant), iodic acid HIO₃ (analytical reagent).' },
    { name: 'Iodine(VII)', uses: 'Periodic acid H₅IO₆ (vicinal diol cleavage in organic synthesis), periodates (selective oxidants).' },
  ],
  54: [
    { name: 'Xenon(II)', uses: 'XeF₂ (powerful electrophilic fluorinating agent in synthesis), spacecraft ion thrusters, fundamental research.' },
  ],
  55: [
    { name: 'Caesium', uses: 'Caesium atomic clocks (definition of the SI second), photoelectric cells, frequency converters, catalysis (Cs₂CO₃).' },
  ],
  56: [
    { name: 'Barium', uses: 'BaSO₄ (radiological contrast agent — barium meal, barium enema), BaTiO₃ (piezoelectric, capacitors), paint filler.' },
  ],
  57: [
    { name: 'Lanthanum(III)', uses: 'Optical glass (high-dispersion lenses, cameras), FCC catalysts for petroleum refining, lanthanum carbonate (phosphate binder in dialysis).' },
  ],
  58: [
    { name: 'Cerium(III)', uses: 'Phosphors (fluorescent lamps, LEDs), optical polishing (CeO₂), automotive three-way catalysts, yellow ceramic colorant.' },
    { name: 'Cerium(IV)', uses: 'Selective oxidant in organic synthesis (CAN, cerium ammonium nitrate), oxidation catalysis, CeO₂ nanoparticles (medicine).' },
  ],
  59: [
    { name: 'Praseodymium(III)', uses: 'Nd-Fe-B permanent magnets (addition for thermal resistance), yellow-green coloured glasses, protective lenses (welders).' },
    { name: 'Praseodymium(IV)', uses: 'PrO₂ oxide (research), bright yellow ceramic colorant compounds.' },
  ],
  60: [
    { name: 'Neodymium(III)', uses: 'Ultra-powerful Nd-Fe-B magnets (electric motors, wind turbines, hard drives), Nd:YAG laser (surgery), purple coloured glass.' },
  ],
  61: [
    { name: 'Promethium(III)', uses: 'Nuclear luminescent paints (¹⁴⁷Pm), space probes (nuclear thermoelectric generators), material thickness gauges. All isotopes radioactive.' },
  ],
  62: [
    { name: 'Samarium(II)', uses: 'Samarium diiodide SmI₂ (Kagan reductant in organic synthesis, selective carbon-carbon couplings).' },
    { name: 'Samarium(III)', uses: 'Sm-Co magnets (high temperature, motors, satellites), catalysts, lasers, bone pain treatment (¹⁵³Sm).' },
  ],
  63: [
    { name: 'Europium(II)', uses: 'Blue phosphors (SrAl₂O₄:Eu²⁺, glow-in-the-dark materials), banknote anti-counterfeiting.' },
    { name: 'Europium(III)', uses: 'Bright red phosphors (colour TV screens, warm-white LEDs), biological fluorescent markers, banknote security.' },
  ],
  64: [
    { name: 'Gadolinium(III)', uses: 'MRI contrast agent (Gd-DTPA complexes, chelated gadolinium), neutron capture therapy (cancer), neutron absorber (reactors).' },
  ],
  65: [
    { name: 'Terbium(III)', uses: 'Bright green phosphors (fluorescent lamps, LEDs), Terfenol-D alloy (giant magnetostriction, acoustic sensors), solid-state laser.' },
    { name: 'Terbium(IV)', uses: 'Mixed oxides for coatings, oxidation catalysis research.' },
  ],
  66: [
    { name: 'Dysprosium(III)', uses: 'High-temperature Nd-Fe-B magnets (Dy additive for electric vehicles and wind turbines), radiation dosimetry, laser.' },
  ],
  67: [
    { name: 'Holmium(III)', uses: 'Ho:YAG laser (urology, orthopaedics, ophthalmology), optical filters (spectrometer calibration), magnets (magnetic poles).' },
  ],
  68: [
    { name: 'Erbium(III)', uses: 'Erbium-doped fibre amplifiers EDFA (Internet telecommunications), Er:YAG laser (dermatology, dentistry), pink glass/ceramic colorant.' },
  ],
  69: [
    { name: 'Thulium(II)', uses: 'Fundamental research, unusual valence states.' },
    { name: 'Thulium(III)', uses: 'Thulium laser (minimally invasive prostate surgery), medical imaging (¹⁶⁹Tm), portable X-ray sources.' },
  ],
  70: [
    { name: 'Ytterbium(II)', uses: 'Reducing agent in organic chemistry (Yb²⁺ analogue of Sm²⁺), photochemical research.' },
    { name: 'Ytterbium(III)', uses: 'Ultra-intense Yb:YAG lasers (laser machining, nuclear fusion), ultra-precise optical clocks (best current time accuracy).' },
  ],
  71: [
    { name: 'Lutetium(III)', uses: 'Lu-DOTA-TATE (¹⁷⁷Lu treatment of neuroendocrine tumours), LSO scintillators (PET scan), catalysis.' },
  ],
  72: [
    { name: 'Hafnium(IV)', uses: 'Nuclear reactor control rods (strong neutron absorption), HfO₂ (high-κ dielectric for nanoscale transistors), superalloys.' },
  ],
  73: [
    { name: 'Tantalum(V)', uses: 'Tantalum capacitors (electronics miniaturisation, smartphones), biocompatible surgical implants, superalloys for reactors and turbines.' },
  ],
  74: [
    { name: 'Tungsten(II)', uses: 'Organometallic complexes, catalysis.' },
    { name: 'Tungsten(IV)', uses: 'WS₂ (high-temperature solid lubricant, MoS₂ analogue), WO₂ (reaction intermediate).' },
    { name: 'Tungsten(VI)', uses: 'Incandescent and halogen lamp filaments, tool steels (WC cemented carbide), catalysts, WO₃ (electrochromism, smart glass).' },
  ],
  75: [
    { name: 'Rhenium(IV)', uses: 'ReS₂ (2D semiconductor), catalysis, coatings.' },
    { name: 'Rhenium(VII)', uses: 'Petroleum reforming catalyst (ReO₃, Re/Pt/Al₂O₃), superalloys for aircraft turbines, rhenium-tungsten thermocouples (very high T).' },
  ],
  76: [
    { name: 'Osmium(II)', uses: 'Grübbs complexes, photochemistry.' },
    { name: 'Osmium(IV)', uses: 'Tetroxide OsO₄ (electron contrast staining in biology, alkene dihydroxylation — Sharpless Nobel 2001).' },
  ],
  77: [
    { name: 'Iridium(III)', uses: 'OLED complexes (phosphorescent organic diodes for screens), asymmetric hydrogenation catalyst, anticancer treatments.' },
    { name: 'Iridium(IV)', uses: 'IrO₂ (electrolysis anode, water electrolysers for green hydrogen), water oxidation catalysis.' },
  ],
  78: [
    { name: 'Platinum(II)', uses: 'Cisplatin (anticancer chemotherapy), catalytic converters, asymmetric hydrogenation catalysis, reference electrodes.' },
    { name: 'Platinum(IV)', uses: 'Carboplatin and oxaliplatin (anticancer), platinum electroplating, advanced catalysis.' },
  ],
  79: [
    { name: 'Gold(I) — Aurous', uses: 'Auranofin compounds (rheumatoid arthritis treatment), electrical contacts (gold alloys), photography (gold chloride).' },
    { name: 'Gold(III) — Auric', uses: 'Powerful catalyst (gold catalysis — alkyne/alkene activation), gold electroplating, aqua regia (dissolution, gold assay).' },
  ],
  80: [
    { name: 'Mercury(I) — Mercurous', uses: 'Calomel (Hg₂Cl₂, SCE reference electrode), historical calomel powder (medical use).' },
    { name: 'Mercury(II) — Mercuric', uses: 'Thimerosal (vaccine preservative), disinfectant (corrosive sublimate — limited use), mercuric chloride (historical antiseptic).' },
  ],
  81: [
    { name: 'Thallium(I)', uses: 'Cardiac scintigraphy (²⁰¹Tl, myocardial perfusion), thallium sulfate (rodenticide — banned in many countries).' },
    { name: 'Thallium(III)', uses: 'Oxidising reagent in organic synthesis (Tl(OAc)₃), IR detection semiconductors (TlBrI = KRS-5).' },
  ],
  82: [
    { name: 'Lead(II)', uses: 'Lead-acid batteries (automotive starters), tin-lead solder (historical electronics), lead crystal, rust-inhibiting paint.' },
    { name: 'Lead(IV)', uses: 'Lead dioxide PbO₂ (positive electrode in Pb-acid battery, strong oxidant), PZT piezoelectric ceramics synthesis.' },
  ],
  83: [
    { name: 'Bismuth(III)', uses: 'Bismuth subsalicylate (Pepto-Bismol®, digestive disorders, H. pylori), pearlescent cosmetics (bismuth oxychloride), lead-free plumbing.' },
    { name: 'Bismuth(V)', uses: 'Bismuth pentafluoride BiF₅ (powerful fluorinating agent), H. pylori quadruple therapy.' },
  ],
  84: [
    { name: 'Polonide', uses: 'Tellurium/selenium polonides (radioisotope research), studied in fundamental chemistry. Highly radioactive.' },
    { name: 'Polonium(II)', uses: 'Industrial static eliminators (photographic film production), nuclear research. ²¹⁰Po highly toxic.' },
    { name: 'Polonium(IV)', uses: 'Actinide/translutonium chemistry research, experimental reactor neutron initiators (Po-Be). Highly radioactive.' },
  ],
  85: [
    { name: 'Astatide', uses: 'Targeted radioimmunotherapy (²¹¹At alpha emitter, thyroid cancer treatment), chemical analogy with iodine and bromine.' },
    { name: 'Astatine(I)', uses: 'Fundamental research on heavy halogens, molecular radiolabelling for PET and therapy.' },
  ],
  86: [],

  87: [
    { name: 'Francium', uses: 'Research on atomic structure and fundamental physics tests (parity violation), laser spectroscopy. Very rare and radioactive.' },
  ],
  88: [
    { name: 'Radium(II)', uses: 'Radium-223 dichloride (Xofigo®, metastatic osteosarcoma treatment), historically: radioluminescent paints (watches), neutron sources.' },
  ],
  89: [
    { name: 'Actinium(III)', uses: 'Generator of ²¹³Bi (alpha therapy for resistant cancers), ²²⁵Ac (bone metastasis treatment, alpha radioimmunotherapy).' },
  ],
  90: [
    { name: 'Thorium(IV)', uses: 'Thorium cycle nuclear reactor fuel (safer, less waste), gas mantle incandescence (Welsbach), magnesium-thorium alloys (aerospace).' },
  ],
  91: [
    { name: 'Protactinium(III)', uses: 'Fundamental research, intermediate in the thorium-uranium nuclear cycle.' },
    { name: 'Protactinium(V)', uses: '²³¹Pa/²³⁰Th geochemical dating (palaeoceanography, past ocean circulation), groundwater migration tracing.' },
  ],
  92: [
    { name: 'Uranium(III)', uses: 'Research, powerful reductant in actinide chemistry.' },
    { name: 'Uranium(IV)', uses: 'Pitchblende (UO₂, natural nuclear fuel), uranium glass (UV fluorescence), ceramics.' },
    { name: 'Uranium(V)', uses: 'Intermediate in uranium enrichment, coordination chemistry.' },
    { name: 'Uranium(VI)', uses: 'UF₆ (uranium enrichment by gaseous diffusion/centrifugation for nuclear fuel), UO₂(NO₃)₂ (nuclear fuel reprocessing).' },
  ],
  93: [
    { name: 'Neptunium(III)', uses: 'Actinide chemistry (research), intermediate in ²³⁸Pu production for RTG space thermoelectric generators.' },
    { name: 'Neptunium(V)', uses: 'Nuclear reprocessing (waste management), nuclear environmental chemistry tracing.' },
  ],
  94: [
    { name: 'Plutonium(III)', uses: 'Actinide chemistry, PUREX reprocessing of spent nuclear fuels.' },
    { name: 'Plutonium(IV)', uses: 'MOX fuel (recycled UO₂/PuO₂ mixed oxide), ²³⁸Pu (RTG space probes: Voyager, Cassini, New Horizons), nuclear weapons (1939).' },
  ],
  95: [
    { name: 'Americium(III)', uses: '²⁴¹Am in ionic smoke detectors (ionisation chamber), radioactive markers, gamma calibration sources.' },
    { name: 'Americium(IV)', uses: 'Actinide chemistry research, separation studies for advanced nuclear reprocessing.' },
  ],
  96: [
    { name: 'Curium(III)', uses: 'Alpha source (²⁴⁴Cm) in space X-ray spectrographs (Mars Science Laboratory Curiosity), fundamental actinide research.' },
  ],
  97: [
    { name: 'Berkelium(III)', uses: 'Bombardment target for synthesis of new superheavy elements (²⁴⁹Bk → Ts, element 117), actinide chemistry research.' },
    { name: 'Berkelium(IV)', uses: 'Fundamental actinide chemistry studies, parallel with terbium (Tb⁴⁺).' },
  ],
  98: [
    { name: 'Californium(II)', uses: 'Analogue of alkaline earth metals, research on reduced oxidation states of actinides.' },
    { name: 'Californium(III)', uses: '²⁵²Cf (spontaneous fission neutron source: reactor start-up, neutron logging for oil drilling, cervical cancer treatment).' },
  ],
  99: [
    { name: 'Einsteinium(II)', uses: 'Research on heavy actinide valence chemistry, parallels with rare earths.' },
    { name: 'Einsteinium(III)', uses: 'Ultra-transuranium actinide chemistry, unique spectroscopic studies (nanogram quantities).' },
  ],
  100: [
    { name: 'Fermium(II)', uses: 'Actinide chemistry research (electronic properties), ion-exchange chromatography experiments with single-atom quantities.' },
    { name: 'Fermium(III)', uses: 'Fundamental studies of heavy actinides, chemical separations of ultra-minimal quantities.' },
  ],
  101: [
    { name: 'Mendelevium(II)', uses: 'Unusual +2 state (like Sm, Eu, Yb), research on unusual actinide properties, superheavy element chemistry (SHE).' },
    { name: 'Mendelevium(III)', uses: 'Solution chemistry studies of heavy actinides, ionic separations at the single-atom scale.' },
  ],
  102: [
    { name: 'Nobelium(II)', uses: 'Most stable state for No (rare earth analogue), superheavy element research, single-atom chemistry.' },
  ],
  103: [
    { name: 'Lawrencium(III)', uses: 'Last actinide, fundamental chemistry of superheavy elements, studies of 5f/6d/7p electronic configuration.' },
  ],
  104: [
    { name: 'Rutherfordium(IV)', uses: 'Single-atom chemistry, validation of relativistic predictions for superheavy elements, quantum chemistry calculations.' },
  ],
  105: [
    { name: 'Dubnium(V)', uses: 'Single-atom chemistry studies (analogy with Ta and Nb), testing relativistic theories for 6d element chemistry.' },
  ],
  106: [
    { name: 'Seaborgium(VI)', uses: 'Gas-phase chemistry experiments with SgO₂Cl₂ (analogy with WO₂Cl₂), confirmation of group 6 behaviour for relativistic elements.' },
  ],
  107: [
    { name: 'Bohrium(VII)', uses: 'Ultra-fast single-atom chemistry (T₁/₂ a few seconds), BhO₃Cl (rhenium analogy), relativistic effects testing.' },
  ],
  108: [
    { name: 'Hassium(VIII)', uses: 'HsO₄ (analogy with OsO₄) detected by gas-phase chemistry, group 8 analogy confirmed, ultra-fast thermochemical studies.' },
  ],
  109: [
    { name: 'Meitnerium(III)', uses: 'Theoretical predictions only (analogy with Ir, Co, Rh), not yet studied chemically, half-life too short.' },
  ],
  110: [
    { name: 'Darmstadtium(II)', uses: 'Theoretical predictions (analogy with Pt, Ni, Pd), chemistry not yet experimentally observed.' },
  ],
  111: [
    { name: 'Roentgenium(I)', uses: 'Theoretical predictions (analogy with Au, Ag, Cu), intense relativistic effects expected, chemistry not yet observed experimentally.' },
  ],
  112: [
    { name: 'Copernicium(II)', uses: 'Predicted volatile behaviour (analogy with Hg, Cd, Zn), gas-phase experiments on Cn-Au reactivity, extreme relativistic effects.' },
  ],
  113: [
    { name: 'Nihonium(I)', uses: 'Theoretical predictions (analogy with Tl but intense relativistic effects), synthesised by RIKEN (Japan 2004), no chemistry observed.' },
  ],
  114: [
    { name: 'Flerovium(II)', uses: 'Theoretical lead analogue but noble-gas behaviour predicted by relativistic theory, some gold-adsorption experiments performed.' },
  ],
  115: [
    { name: 'Moscovium(III)', uses: 'Theoretical analogy with bismuth and arsenic, JINR-LLNL synthesis 2003, no chemistry performed, half-life < 1 s.' },
  ],
  116: [
    { name: 'Livermoride', uses: 'Theoretical analogue of polonium (and sulfur-selenium-tellurium), chalcogen behaviour predicted, chemistry not observed.' },
  ],
  117: [
    { name: 'Tennessine(−I)', uses: 'Theoretical halogen analogue but relativistic effects may favour +1 as the more stable state, chemistry not observed, synthesised 2010.' },
  ],
  118: [
    { name: 'Oganesson', uses: 'Heaviest confirmed element (Z=118). Chemical behaviour unpredictable: relativistic effects may make it non-noble-gas. Synthesis 2002 (4 atoms observed).' },
  ],
};
